import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Stack } from '@mui/material';
import { listSites, createSite, updateSite, deleteSite } from '../services/sites';
import { fetchCurrentUser } from '../services/auth';
import api from '../services/api';

const SitesPage = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', director_id: '' });
  const [edit, setEdit] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    (async () => {
      const me = await fetchCurrentUser();
      setIsAdmin(me?.role === 1);
      const res = await listSites();
      const sites = (res.data || []).map((s) => ({
        ...s,
        director_name: s.director ? `${s.director.first_name || ''} ${s.director.last_name || ''}`.trim() : '—',
      }));
      setRows(sites);
      try {
        const emps = await api.get('/employees');
        // Trier les employés par nom (first_name puis last_name)
        const sortedEmployees = (emps.data || []).sort((a, b) => {
          const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
          const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setEmployees(sortedEmployees);
      } catch (e) {
        // ignore silently, dropdown will be empty
      }
    })();
  }, []);

  const onOpen = (row = null) => {
    setEdit(row);
    setForm(row ? { name: row.name, address: row.address || '', director_id: row.director_id || '' } : { name: '', address: '', director_id: '' });
    setOpen(true);
  };
  const onClose = () => setOpen(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSave = async () => {
    if (edit) {
      const payload = { ...form, director_id: form.director_id ? form.director_id : null };
      const res = await updateSite(edit.id, payload);
      const updated = {
        ...res.data,
        director_name: res.data.director ? `${res.data.director.first_name || ''} ${res.data.director.last_name || ''}`.trim() : (employees.find(e => String(e.id) === String(payload.director_id)) ? `${employees.find(e => String(e.id) === String(payload.director_id)).first_name} ${employees.find(e => String(e.id) === String(payload.director_id)).last_name}` : '—')
      };
      setRows(rows.map(r => (r.id === edit.id ? updated : r)));
    } else {
      const payload = { ...form, director_id: form.director_id ? form.director_id : null };
      const res = await createSite(payload);
      const added = {
        ...res.data,
        director_name: (employees.find(e => String(e.id) === String(payload.director_id)) ? `${employees.find(e => String(e.id) === String(payload.director_id)).first_name} ${employees.find(e => String(e.id) === String(payload.director_id)).last_name}` : '—')
      };
      setRows([...rows, added]);
    }
    setOpen(false);
  };

  const onDelete = async (id) => {
    if (!window.confirm('Supprimer ce site ?')) return;
    await deleteSite(id);
    setRows(rows.filter(r => r.id !== id));
  };

  const columns = [
    { field: 'name', headerName: 'Nom', flex: 1, headerClassName: 'super-app-theme--header' },
    { field: 'address', headerName: 'Adresse', flex: 1, headerClassName: 'super-app-theme--header' },
    { field: 'director_name', headerName: 'Directeur de site', flex: 1, headerClassName: 'super-app-theme--header' },
    ...(isAdmin ? [{
      field: 'actions', headerName: 'Actions', width: 180, headerClassName: 'super-app-theme--header', renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 1 }}>
          <Button size="small" onClick={() => onOpen(params.row)}>Modifier</Button>
          <Button size="small" color="error" onClick={() => onDelete(params.row.id)}>Supprimer</Button>
        </Stack>
      )
    }] : [])
  ];

  return (
    <div style={{ height: 520, width: '100%' }}>
      <h2>Sites</h2>
      {isAdmin && (
        <Button variant="contained" onClick={() => onOpen()} sx={{ mb: 2 }}>Nouveau site</Button>
      )}
      <DataGrid 
        rows={rows} 
        columns={columns} 
        getRowId={(r) => r.id} 
        pageSize={10}
        getRowHeight={() => 80}
        sx={{
          '& .super-app-theme--header': {
            backgroundColor: '#1976d2',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
          },
          '& .MuiDataGrid-root': {
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#1976d2',
            fontSize: '14px',
            fontWeight: 600,
            color: 'white',
            borderBottom: '2px solid #1565c0',
          },
          '& .MuiDataGrid-columnHeader': {
            '&:focus': {
              outline: 'none',
            },
            '&:hover': {
              backgroundColor: '#1565c0',
            },
          },
          '& .MuiDataGrid-cell': {
            fontSize: '13px',
            padding: '16px 8px',
            borderBottom: '1px solid #f0f0f0',
            '&:focus': {
              outline: 'none',
            },
          },
          '& .MuiDataGrid-row': {
            '&:nth-of-type(even)': {
              backgroundColor: '#fafafa',
            },
            '&:hover': {
              backgroundColor: '#e3f2fd',
            },
            '&.Mui-selected': {
              backgroundColor: '#e3f2fd',
            },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-pagination': {
            '& .MuiIconButton-root': {
              borderRadius: '4px',
            },
          },
        }}
      />

      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{edit ? 'Modifier le site' : 'Nouveau site'}</DialogTitle>
        <DialogContent>
          <TextField margin="dense" fullWidth label="Nom" name="name" value={form.name} onChange={onChange} />
          <TextField margin="dense" fullWidth label="Adresse" name="address" value={form.address} onChange={onChange} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Directeur de site</InputLabel>
            <Select label="Directeur de site" name="director_id" value={form.director_id} onChange={onChange}>
              <MenuItem value="">Aucun</MenuItem>
              {employees.map((e) => (
                <MenuItem key={e.id} value={e.id}>{`${e.first_name || ''} ${e.last_name || ''}`.trim()}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button onClick={onSave} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SitesPage;
